import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Divider, Icon, IconButton, ListItemIcon, Menu, MenuItem, Paper, Snackbar, Tooltip } from '@mui/material';
import { useIsAuthenticated } from '@azure/msal-react';

import ProgressBar from '../../controls/ProgressBar';
import NoActiveOrganisatieMessage from '../../controls/NoActiveOrganisatieMessage';
import DialogPortfolioStart from '../../controls/dialogs/DialogPortfolioStart';
import DialogPortfolioAddPlans from '../../controls/dialogs/DialogPortfolioAddPlans';
import DialogPortfolioConsortiums from '../../controls/dialogs/DialogPortfolioConsortiums';
import OverzichtPlannen from './OverzichtPlannen';
import OverzichtAnnotations from './OverzichtAnnotations';
import AzureMap from '../../controls/AzureMaps';


import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { Portfolio } from '../../../models/portfolio/portfolio';

import { userManagementService } from '../../../services/user-management.service';
import { eventBus } from '../../../services/event-emitter.service';
import { PortfolioService, portfolioService } from '../../../services/portfolio.service';
import { authService } from '../../../services/auth.service';
import { mapService } from '../../../services/map.service';
import { InclusionRequestStatusEnum } from '../../../content/inclusionRequestStatus';

import TaskSwimlanes from './TaskSwimlanes';
import ErrorMessage from '../../controls/ErrorMessage';
import { navigateTo } from '../../../services/utils/navigation';


interface PortfolioDetailProps {
  portfolioId?: string; // ID of the current portfolio
  zoomInOnPlannenBbox?: (bbox: any) => void;
  refetchPortfolios?: () => void;
}

const PortfolioDetail: React.FC<PortfolioDetailProps> = ({
  portfolioId: pId,
  zoomInOnPlannenBbox,
  refetchPortfolios
}) => {
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  const { id: urlPortfolioId } = useParams<{ id: string }>(); // Get ID from URL

  // State to store the list of plans
  const [portfolio, setPortfolio] = useState<Portfolio>({} as Portfolio);
  const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);
  const [organizations, setOrganizations] = useState<any[] | null>(null);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [preselectedPlanId, setPreselectedPlanId] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    snackbar: false,
    plan: false,
    portfolio: false,
    consortiums: false,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menu, setOpenMenu] = useState({
    plan: false,
    portfolio: false,
    consortium: false,
    annotation: false,
    task: false,
  });

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open, menuToClose?: keyof typeof menu) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
    if (menuToClose) {
      closeMenu(menuToClose);
    }
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, name: keyof typeof menu) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(prev => ({ ...prev, [name]: true }));
  };

  const closeMenu = (name: keyof typeof menu) => {
    setAnchorEl(null);
    setOpenMenu(prev => ({ ...prev, [name]: false }));
  };

  const openDialogByEmit = (dialog: string, menuToClose?: keyof typeof menu) => {
    eventBus.emit('openDialog', dialog);

    if (menuToClose) {
      closeMenu(menuToClose);
    }
  };

  // Ref to prevent fetching data twice in Strict Mode
  const hasFetchedData = useRef(false);

  const portfolioId = pId || urlPortfolioId; // Use prop ID if available, otherwise URL ID
  const canCrud = !isReadOnly;
  const statusOption = PortfolioService.STATUS_OPTIONS.find(option => option.id === portfolio?.afstemmingsStatus);
  const statusIcon = statusOption ? statusOption.icon : '';
  const statusLabel = statusOption ? statusOption.name : '-';
  const statusTooltipText = statusOption ? `Afstemmingsstatus: ${statusOption.name} - ${statusOption.tooltip}` : 'Geen status beschikbaar';

  useEffect(() => {
    setIsReadOnly(authService.isReadOnly());
  }, []);

  const getActuelePlannenBbox = useCallback((portfolioData?: Portfolio | null): [number, number, number, number] | null => {
    if (!portfolioData?.plans?.length) return null;

    const actuelePlanBboxes = portfolioData.plans
      .filter((plan: any) => {
        const status = (plan?.inclusionRequestStatus ?? '').toLowerCase();
        return status === InclusionRequestStatusEnum.Accepted || status === InclusionRequestStatusEnum.Pending;
      })
      .map((plan: any) => plan?.bbox)
      .filter((bbox: unknown): bbox is [number, number, number, number] => mapService.isValidBbox(bbox));

    if (!actuelePlanBboxes.length) return null;

    return mapService.calculatedBoundingBox(actuelePlanBboxes as number[][]) as [number, number, number, number] | null;
  }, []);

  useEffect(() => {
    const actueleBbox = getActuelePlannenBbox(portfolio);

    if (actueleBbox) { // ipv portfolio.bbox
      // Wait a tick to ensure AzureMap is mounted
      const timeout = setTimeout(() => {
        eventBus.emit('zoomInOnPlanBbox', actueleBbox); // ipv portfolio.bbox
        eventBus.emit('drawPortfolioBbox', actueleBbox);
      }, 50);

      return () => clearTimeout(timeout);
    }
  }, [portfolio, getActuelePlannenBbox]);

  useEffect(() => {
    const handleSelectPlan = (planId: string | null) => {
      setSelectedPlanId(planId);
    };
    eventBus.on('selectPlan', handleSelectPlan);
    return () => {
      eventBus.off('selectPlan', handleSelectPlan);
    };
  }, []);

  const getOrganizations = async (data: any) => {
    // const acceptedPlans = data.plans.filter((plan: { inclusionRequestStatus: string }) => plan.inclusionRequestStatus === 'accepted');

    const uniqueOrganizations = Object.values(
      data.plans.reduce(
        (
          acc: Record<string, { id: string; name: string; active: boolean }>,
          plan: { organizationId: string; organizationName: string; inclusionRequestStatus: string }
        ) => {
          const id = plan.organizationId;
          if (!acc[id]) {
            acc[id] = {
              id: plan.organizationId,
              name: plan.organizationName,
              active: (plan.inclusionRequestStatus === 'accepted'),
            };
          }
          return acc;
        },
        {}
      )
    );

    setOrganizations(uniqueOrganizations);
  };

  const fetchPortfolio = useCallback(async () => {
    try {
      if (portfolioId) {
        setLoading(true);
        const data = await portfolioService.getPortfolioIncludingCalculatedBbox(portfolioId); // getPortfolioInclPlanData

        if (data) {
          setPortfolio(data);
          getOrganizations(data);

          const actueleBbox = getActuelePlannenBbox(data as Portfolio);
          if (actueleBbox) {
            eventBus.emit('zoomInOnPlanBbox', actueleBbox);
            eventBus.emit('drawPortfolioBbox', actueleBbox);
          }

          // Clear selection when viewing a portfolio
          eventBus.emit('selectPlan', null);
        }

        setLoading(false);
      }
    } 
    catch (error: any) {
      const rawResponse = error?.response?.data;

      const errorMessage =
        typeof rawResponse === 'string'
          ? rawResponse
          : error?.message || 'Er is een fout opgetreden';

      if (error.status === 401 || error.status === 403) {
        navigateTo("/niet-toegestaan");
        setError('Als je wil deelnemen aan dit afstemmingsdossier, neem dan contact op met een organisatie die aan dit dossier deelneemt.');

        setLoading(false);
        return;
      }

      if (error.status === 500 && errorMessage.includes('is not part of')) {
        navigateTo("/niet-toegestaan");
        setError('Als je wil deelnemen aan dit afstemmingsdossier, neem dan contact op met een organisatie die aan dit dossier deelneemt.');

        setLoading(false);
        return;
      }

      // Algemene fout
      setError(errorMessage || 'Er is een fout opgetreden');
      setLoading(false);
    }
  }, [portfolioId, getActuelePlannenBbox]);

  const triggerReloadPortfolio = useCallback(async (_status: string) => {
    await fetchPortfolio();
  }, [fetchPortfolio]);

  // Fetch the plans data when the component mounts
  useEffect(() => {
    if (!portfolioId) return;

    const storedOrganisatie = userManagementService.getStoredOrganisatie();
    if (storedOrganisatie) {
      setMijnOrganisatie(storedOrganisatie);
    }

    if (!hasFetchedData.current) {
      fetchPortfolio();
      hasFetchedData.current = true; // Mark as fetched
    }
  }, [portfolioId, fetchPortfolio]); // Run whenever infplId changes // [] means only once

  const onSubmitPlan = useCallback(async (infplId: string) => {
    try {
      if (portfolioId && infplId) {
        setLoading(true);

        const response = await portfolioService.addPlanDeelnameToPortfolio(infplId, portfolioId);
        if (!response) {
          setLoading(false);
          return;
        }

        if (response) {
          setTimeout(async () => {
            setSnackbarMessage('Plan toegevoegd aan afstemmingsdossier');
            setOpenDialogs((prev) => ({ ...prev, snackbar: true }));

            await fetchPortfolio();
            setLoading(false);
          }, 3000);
        }
      }
    }
    catch (error: any) {
      if (error.status === 422) {
        setError('Dit plan is al eerder toegevoegd aan dit afstemmingsdossier.');
      } 
      else {
        setError(error.message || 'Er is een fout opgetreden');
      }
      setLoading(false);
    }
  }, [portfolioId, fetchPortfolio]);

  const addPlanToPortfolio = useCallback(async (infplId: string, currrentPortfolioId: string) => {
    if (portfolioId !== currrentPortfolioId) return;

    await onSubmitPlan(infplId);
  }, [portfolioId, onSubmitPlan]);

  const openAddPlanDialog = useCallback((infplId: string, currrentPortfolioId: string) => {
    if (portfolioId !== currrentPortfolioId) return;

    setPreselectedPlanId(infplId);
    setOpenDialogs((prev) => ({ ...prev, plan: true }));
  }, [portfolioId]);

  useEffect(() => {
    eventBus.on('reloadPortfolio', triggerReloadPortfolio);
    eventBus.on('addPlanToPortfolio', addPlanToPortfolio);
    eventBus.on('openAddPlanDialog', openAddPlanDialog);

    // Cleanup
    return () => {
      eventBus.off('reloadPortfolio', triggerReloadPortfolio);
      eventBus.off('addPlanToPortfolio', addPlanToPortfolio);
      eventBus.off('openAddPlanDialog', openAddPlanDialog);
    };
  }, [addPlanToPortfolio, openAddPlanDialog, triggerReloadPortfolio]);

  const currentOrganizationHasAcceptedPlans = () => {
    if (!portfolio || !mijnOrganisatie) return false;

    const activePlans = portfolio.plans?.filter(plan =>
      plan.organizationId === mijnOrganisatie.id && plan.inclusionRequestStatus === 'accepted'
    );

    return activePlans && activePlans.length > 0;
  };

  const handleSubmitPortfolio = (portfolio: Portfolio) => {
    handlePortfolio(portfolio);
    closeDialog('portfolio');
  };

  const handlePortfolio = async (portfolio: Portfolio) => {
    if (!portfolio) {
      console.error('Portfolio is null');
      return;
    }

    try {
      setLoading(true);
      const response = await portfolioService.updatePortfolio(portfolio);

      if (!response) {
        setLoading(false);
        // Handle error based on HTTP status code
        const errorMessage = await response.text();
        console.error('Failed to update portfolio:', errorMessage);
        throw new Error(errorMessage);
      }

      setTimeout(() => {
        setLoading(false);
        setSnackbarMessage('Afstemmingsdossier bijgewerkt');
        openDialog('snackbar');

        fetchPortfolio();
      }, 4000);

    }
    catch (error) {
      setLoading(false);
      console.error('Error updating portfolio:', error);
      setError('Er is een fout opgetreden');
    }
  }

  if (!mijnOrganisatie) {
    return (
      <>
        <NoActiveOrganisatieMessage />
      </>
    )
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      {isAuthenticated && portfolio && mijnOrganisatie && (
        <div className="text-left">
          {/* <pre>
            <code>{JSON.stringify(plan, null, 2)}</code>
          </pre> */}

          <div className="container container-fluid">
            <div className="row portfolio-detail-summary-row">
              <div className="col-12 col-xl-4 col-lg-6 portfolio-detail-summary-col">
                <Paper className="portfolio-detail-summary-panel" sx={{ width: '100%', padding: '20px', margin: '0px auto 30px' }}>
                  <div className="navigation nav-functions nav-functions-clear nav-functions-left nav-functions-in-panel">
                    <IconButton
                      color="secondary"
                      size="small"
                      onClick={() => navigate('/dossiers')}
                    >
                      <Tooltip title="Terug naar afstemmingsdossiers" arrow>
                        <Icon>arrow_backward</Icon>
                      </Tooltip>
                    </IconButton>

                    {portfolio.activeParticipant && canCrud && (
                      <>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={(event) => openMenu(event, 'portfolio')}
                        >
                          <Tooltip title="Bewerk het afstemmingsdossier" arrow>
                            <Icon>work</Icon>
                          </Tooltip>
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={menu.portfolio}
                          onClose={() => closeMenu('portfolio')}
                        >
                          <MenuItem onClick={() => {
                            openDialog('portfolio');
                            closeMenu('portfolio');
                          }}>
                            <ListItemIcon>
                              <Icon>edit</Icon>
                            </ListItemIcon>
                            Wijzigen
                          </MenuItem>
                        </Menu>


                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={(event) => openMenu(event, 'plan')}
                        >
                          <Tooltip title="Uitnodigen plannen" arrow>
                            <Icon>map</Icon>
                          </Tooltip>
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={menu.plan}
                          onClose={() => closeMenu('plan')}
                        >
                          <MenuItem onClick={() => {
                            openDialog('plan');
                            closeMenu('plan');
                          }}>
                            <ListItemIcon>
                              <Icon>add</Icon>
                            </ListItemIcon>
                            Plan toevoegen
                          </MenuItem>
                        </Menu>

                        {currentOrganizationHasAcceptedPlans() && (
                          <>
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={(event) => openMenu(event, 'annotation')}
                            >
                              <Tooltip title="Voeg notities toe" arrow>
                                <Icon>chat</Icon>
                              </Tooltip>
                            </IconButton>
                            <Menu
                              anchorEl={anchorEl}
                              open={menu.annotation}
                              onClose={() => closeMenu('annotation')}
                            >
                              <MenuItem onClick={() => {
                                openDialogByEmit('annotation', 'annotation');
                              }}>
                                <ListItemIcon>
                                  <Icon>add</Icon>
                                </ListItemIcon>
                                Notitie toevoegen
                              </MenuItem>
                            </Menu>

                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={(event) => openMenu(event, 'task')}
                            >
                              <Tooltip title="Voeg acties toe" arrow>
                                <Icon>assignment</Icon>
                              </Tooltip>
                            </IconButton>
                            <Menu
                              anchorEl={anchorEl}
                              open={menu.task}
                              onClose={() => closeMenu('task')}
                            >
                              <MenuItem onClick={() => {
                                openDialogByEmit('task', 'task');
                              }}>
                                <ListItemIcon>
                                  <Icon>add</Icon>
                                </ListItemIcon>
                                Actie toevoegen
                              </MenuItem>
                            </Menu>
                          </>
                        )}
                      </>
                    )}

                    {portfolio.activeParticipant && (
                      <>
                        <IconButton
                          color="secondary"
                          size="small"
                          onClick={(event) => openMenu(event, 'consortium')}
                        >
                          <Tooltip title={isReadOnly ? 'Bekijk samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'} arrow>
                            <Icon>groups</Icon>
                          </Tooltip>
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={menu.consortium}
                          onClose={() => closeMenu('consortium')}
                        >
                          <MenuItem onClick={() => {
                            openDialog('consortiums');
                            closeMenu('consortium');
                          }}>
                            <ListItemIcon>
                              <Icon>{isReadOnly ? 'list' : 'edit'}</Icon>
                            </ListItemIcon>
                            {isReadOnly ? 'Bekijk samenwerkingsverbanden' : 'Samenwerkingsverbanden beheren'}
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </div>

                  {portfolio.afstemmingsStatus && (
                    <Tooltip title={statusTooltipText} placement="right" arrow>
                      <span className={`afstemmings-status-icon status-icon-with-label status-icon-40 ${portfolio.afstemmingsStatus}`}>
                        <span className="material-icons material-symbols-rounded">{statusIcon}</span>
                        <span className="status-icon-label">{statusLabel}</span>
                      </span>
                    </Tooltip>
                  )}

                  <div className="portfolio-detail-summary-content">
                    <label>Naam</label>
                    <h1 className="portfolio-name">{portfolio.name}</h1>

                    {portfolio.description && (
                      <div className="portfolio-description">
                        <label>Omschrijving</label>
                        <p className="portfolio-description-scroll">{portfolio.description || '-'}</p>
                      </div>
                    )}
                  </div>
                </Paper>
              </div>

              <div className="col-12 col-xl-8 col-lg-6 portfolio-detail-map-col">
                <Paper className="portfolio-detail-map-panel" sx={{ width: '100%', margin: '0px auto 30px' }}>
                  <div className="map-placeholder">
                    { portfolio?.bbox ? (
                      <AzureMap portfolioId={portfolio?.id ?? undefined} portfolioActiveParticipant={portfolio.activeParticipant} selectedPlanId={selectedPlanId} onSelectPlan={setSelectedPlanId} />
                    ) : (
                      <span>Kaartweergave</span>
                    )}
                  </div>
                </Paper>
              </div>

              <div className="col-12 col-sm-12">
                <div className="row">
                  <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-relative">
                    { portfolio?.plans && (
                      <>
                        {portfolio.activeParticipant && canCrud && (
                          <div className="btn-group btn-group-right">
                            <Tooltip title="Plan uitnodigen voor dit dossier" placement="right" arrow>
                              <Button variant="text" color="secondary" onClick={ () => openDialog('plan') }>
                                <Icon>add</Icon>
                                Plan
                              </Button>
                            </Tooltip>
                          </div>
                        )}
                        <OverzichtPlannen
                          plannen={portfolio?.plans}
                          activeParticipant={portfolio.activeParticipant}
                          canCrud={canCrud}
                          mijnOrganisatieId={mijnOrganisatie.id} />
                      </>
                    )}
                  </div>

                  {/* <div className="col-12 col-sm-12 col-md-12 col-lg-4">
                    <OverzichtPortfolioConsortiums
                      consortiums={portfolio?.consortiums}
                      canManage={portfolio.activeParticipant}
                      onAddClick={() => openDialog('consortiums')}
                    />
                  </div> */}
                </div>

                <Divider className="divider divider-10 divider-transparent" />
              </div>
            </div>
          </div>

          <div className="container container-fluid">
            <div className="row">
              {portfolio?.id && mijnOrganisatie?.id && (
                <>
                  {!currentOrganizationHasAcceptedPlans() && (
                    <div className="col-12 col-sm-12">
                      <div className="melding">
                        <Icon>info</Icon>
                        <span>
                          Uw organisatie neemt nog niet actief deel aan dit afstemmingsdossier. Daardoor zijn acties en notities nog niet beschikbaar.
                        </span>
                      </div>
                    </div>
                  )}

                  {currentOrganizationHasAcceptedPlans() && (
                    <>
                      <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-relative">
                        <OverzichtAnnotations
                          portfolioId={portfolio.id}
                          mijnOrganisatieId={mijnOrganisatie.id}
                          organizations={organizations || []}
                          readOnly={isReadOnly}
                        />
                      </div>
                      <div className="col-12 col-sm-12 col-md-12 col-lg-6 col-relative">
                        {organizations && (
                          <TaskSwimlanes
                            portfolioId={portfolio.id}
                            mijnOrganisatieId={mijnOrganisatie.id}
                            organizations={organizations}
                            readOnly={isReadOnly}
                          />
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      { portfolio && (
        <>
          <DialogPortfolioStart
            open={open.portfolio}
            portfolioId={portfolio.id || undefined}
            onClose={ () => closeDialog('portfolio') }
            onSubmit={handleSubmitPortfolio} />

          <DialogPortfolioAddPlans
            open={open.plan}
            portfolio={portfolio}
            preselectedPlanId={preselectedPlanId}
            onClose={ () => { closeDialog('plan'); setPreselectedPlanId(null); } }
            onSubmit={onSubmitPlan} />

          <DialogPortfolioConsortiums
            open={open.consortiums}
            portfolioId={portfolio.id || undefined}
            onClose={() => closeDialog('consortiums')}
            onUpdated={fetchPortfolio}
          />
        </>
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

      { loading && (
        <ProgressBar />
      )}
    </>
  );
};

export default PortfolioDetail;